/* eslint-disable no-undef */
import { put, call } from 'redux-saga/effects';
import axios from 'src/api/Api';
import {
    GET_HYBRIS_USER_DETAILS_SUCCESS,
    GET_HYBRIS_USER_DETAILS_FAILURE,
    LOGIN_SUCCESS,
    LOGIN_FAILURE,
} from './../actions/constants';
import { errorHandler } from 'src/errorHandler/errorHandler';
import {
    setUserData,
    validateUserRole,
    clearPersistStorage,
    checkImpersonationActive,
    checkProxyActive,
} from 'src/utils/util';
import { getAuthConfig } from 'src/utils/authConfig';
import { getImpersonationUserData } from 'src/pages/impersonation/redux/actions';
import { getConfigurations, getBannerDetails } from './../actions';
import { getPageVisibilityRule } from 'src/pages/home/redux/actions';
import {
    resetAppliedOrderListFilter,
    resetOrderListFilter,
    resetAwaitingToggle,
    resetAwaitingToggleMobile,
} from 'src/pages/orders/redux/actions/index';
import { resetCalendarSelectedDate } from 'src/pages/home/redux/actions/index';
import { resetAccountFilterState } from 'src/pages/more/redux/actions';
import {
    IMPERSONATED_URL,
    JAPAN_SALESORG,
    MENU,
} from 'src/constants/appConstants';
import i18n from 'src/i18n';
import { logUiErrorToBackend } from 'src/utils/logUiErrorToBackend';

/**
 * Builds token request config based on auth style (derived from BASE_URL).
 * On-prem (.wcms.): query params, Bearer header, tokenField 'value'
 * CCV2: URLSearchParams body, Basic auth, tokenField 'access_token'
 */
const getTokenConfig = ({ username, password, ssoToken }) => {
    const { tokenUrl, tokenField, useBasicAuth } = getAuthConfig();
    const isSSO = !!ssoToken;

    if (useBasicAuth) {
        // CCV2 style
        const params = new URLSearchParams();
        params.append('grant_type', 'custom');
        params.append('scope', 'extended');
        if (!isSSO) params.append('username', username);

        const headers = {
            Authorization: `Basic ${btoa(process.env.CLIENT_ID_HYBRIS + ':' + process.env.CLIENT_SECRET_HYBRIS)}`,
            'Content-Type': 'application/x-www-form-urlencoded',
        };
        if (isSSO) {
            headers.idptoken = 'ssotoken';
            headers['Authorization-SSO'] = `Bearer ${ssoToken}`;
        } else {
            headers.password = password;
        }

        return { url: tokenUrl, body: params, headers, tokenField };
    } else {
        // On-prem style
        const url = `${tokenUrl}?client_id=${process.env.CLIENT_ID_HYBRIS}&client_secret=${process.env.CLIENT_SECRET_HYBRIS}&grant_type=custom&scope=extended`;
        const headers = {};
        if (isSSO) {
            headers.idptoken = 'ssotoken';
            headers.Authorization = `Bearer ${ssoToken}`;
        } else {
            headers.username = username;
            headers.password = password;
        }

        return { url, body: {}, headers, tokenField };
    }
};

/**
 * Fetches user details from IMPERSONATED_URL and dispatches configs.
 */
function* fetchUserDetailsAndConfigure(isLoginFlow) {
    const result = yield call([axios, 'get'], IMPERSONATED_URL);
    const userData = result.data;

    if (checkImpersonationActive() || checkProxyActive()) {
        yield put(getImpersonationUserData());
    }

    setUserData(userData);

    const isJapanLanguageChanged =
        userData?.englishPreferred != 'Y' &&
        userData?.languageISOCode === 'ja' &&
        userData?.salesOrgs &&
        userData?.salesOrgs[0] === JAPAN_SALESORG;

    isJapanLanguageChanged ? i18n.changeLanguage('ja') : i18n.changeLanguage('en');

    yield put({
        type: GET_HYBRIS_USER_DETAILS_SUCCESS,
        payload: {
            ...userData,
            isLanguageChanged: isJapanLanguageChanged,
        },
    });

    if (validateUserRole(userData.employeeRole)) {
        localStorage.setItem('userAuthenticated', true);
    }

    yield put(getConfigurations({ resourceType: 'PROCESS' }, MENU));
    yield put(getPageVisibilityRule({ fields: 'BASIC' }));
    yield put(getBannerDetails());

    if (isLoginFlow) {
        clearPersistStorage();
        yield put(resetAppliedOrderListFilter());
        yield put(resetOrderListFilter());
        yield put(resetCalendarSelectedDate());
        yield put(resetAwaitingToggle());
        yield put(resetAwaitingToggleMobile());
        yield put(resetAccountFilterState());
    }
}

/**
 * Login saga — handles token fetch + user details.
 * Dispatched by both login page (credentials) and AccessToken (SSO).
 *
 * action.payload: { username?, password?, ssoToken?, isLoginFlow }
 */
export function* loginSaga(action) {
    const { isLoginFlow = true } = action.payload;

    try {
        const config = getTokenConfig(action.payload);
        const response = yield call([axios, 'post'], config.url, config.body, {
            headers: config.headers,
        });

        const authToken = response.data[config.tokenField];
        localStorage.setItem('authToken', authToken);

        yield call(fetchUserDetailsAndConfigure, isLoginFlow);
        yield put({ type: LOGIN_SUCCESS });
    } catch (error) {
        logUiErrorToBackend(error, {
            apiEndPoint: error?.config?.url || '',
            requestPayload: action.payload.username ? { username: action.payload.username } : undefined,
        });
        localStorage.removeItem('userAuthenticated');

        const errorMessage = error?.response?.data?.errors?.[0]?.message || '';
        yield put({
            type: LOGIN_FAILURE,
            payload: errorMessage,
        });
        yield put({
            type: GET_HYBRIS_USER_DETAILS_FAILURE,
            templateError: errorHandler('503'),
        });
    }
}
